/* eslint-disable no-param-reassign */
import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { IPartner } from 'pages/PayCms/MasterTables/utils/interface'
import { get } from 'utils/apiClient'
import { IApiResponse } from 'utils/apiInterfaces'
import apiEndPoints from 'utils/constants/apiEndpoints'

interface ICountry {
  countryName: string
  countryCode: string
  code: number
  countryId: number
  modifiedDate: string
  status: string
}

interface ICountryList {
  countryName: string
  countryCode: string
  code: number
  countryId: number
  modifiedDate: string
  status: string
}

interface IProduct {
  productId: number
  productName: string
  productCode: string
  modifiedBy: number
  modifiedDate: string
  status: string
}

interface IPlatform {
  platformId: number
  platformName: string
  platformCode: string
  appCode: null
  keyName: string
  modifiedBy: number
  modifiedDate: string
  status: string
}

interface IPaymentProvider {
  providerName: string
  providerImage: string
  paymentMode: number
  additional: null
  verifyPaymentStatus: number
  json: string
  communicationTrigger: null
  providerId: number
  modifiedBy: number
  modifiedDate: string
  status: string
}

interface IPaymentMode {
  paymentId: number
  paymentName: number
  paymentImage: string
  modifiedBy: number
  modifiedDate: string
  status: string
}

interface IContentPartner {
  contentPartnerId: string
  contentPartnerName: string
}

interface ISystem {
  id: string
  systemName: string
}

interface IAdPolicy {
  policyId: string
  description: string
}

interface ICommonData {
  countries: { label: string; value: number }[]
  countryList: ICountryList[]
  products: { label: string; value: string | number }[]
  platforms: { label: string; value: string | number; platformCode: string }[]
  paymentProviders: { label: string; value: string | number }[]
  mandate: { label: string; value: string }[]
  paymentModes: { label: string; value: string }[]
  contentPartners: { label: string; value: string }[]
  systems: { label: string; value: string }[]
  adPolicies: { label: string; value: string }[]
  partners: IPartner[]
}

interface IPlanMetaData {
  contentPartners: IContentPartner[]
  systems: ISystem[]
  adPolicies: IAdPolicy[]
}

export const fetchCountryList = createAsyncThunk(
  'auth/fetchCountryList',
  async () => {
    const countriesData: IApiResponse<ICountry[]> = await get(
      `${apiEndPoints.COUNTRY_LIST}?page=1&limit=1000`
    )
    return countriesData.data
  }
)

export const fetchProductList = createAsyncThunk(
  'auth/fetchProductsList',
  async () => {
    const productData: IApiResponse<IProduct[]> = await get(
      `${apiEndPoints.PRODUCT_LIST}?page=1&limit=1000`
    )
    return productData.data
  }
)

export const fetchPlatformList = createAsyncThunk(
  'auth/fetchPlatformList',
  async () => {
    const platformData: IApiResponse<IPlatform[]> = await get(
      `${apiEndPoints.PLATFORM_LIST}?page=1&limit=1000`
    )
    return platformData.data
  }
)

export const fetchPaymentProviderList = createAsyncThunk(
  'auth/fetchPaymentProviderList',
  async () => {
    const providerData: IApiResponse<IPaymentProvider[]> = await get(
      `${apiEndPoints.PROVIDER_LIST}?page=1&limit=1000`
    )
    return providerData.data
  }
)

export const fetchPaymentModeList = createAsyncThunk(
  'auth/fetchPaymentModeList',
  async () => {
    const modeData: IApiResponse<IPaymentMode[]> = await get(
      `${apiEndPoints.MODE_LIST}?page=1&limit=1000`
    )
    return modeData.data
  }
)

export const fetchPlanMetaData = createAsyncThunk(
  'auth/fetchPlanMetaData',
  async () => {
    const modeData: IApiResponse<IPlanMetaData> = await get(
      `${apiEndPoints.PLAN_META_DATA}`
    )
    return modeData.data
  }
)

export const fetchTvodTiers = createAsyncThunk(
  'auth/fetchTvodTiers',
  async () => {
    const modeData: IApiResponse<IPlanMetaData> = await get(
      `${apiEndPoints.PLAN_META_DATA}`
    )
    return modeData.data
  }
)

const initialState: ICommonData = {
  countries: [],
  countryList: [],
  products: [],
  platforms: [],
  paymentProviders: [],
  mandate: [],
  paymentModes: [],
  contentPartners: [],
  systems: [],
  adPolicies: [],
  partners: [],
  // categories: [],
}

const commonDataSlice = createSlice({
  name: 'commonData',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder

      .addCase(fetchCountryList.pending, (state) => {
        state.countries = []
      })
      .addCase(
        fetchCountryList.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.countryList = action.payload
          state.countries = action.payload.map((country: ICountry) => ({
            label: country.countryName,
            value: country.countryId,
          }))
        }
      )
      .addCase(fetchCountryList.rejected, (state) => {
        state.countries = []
        state.countryList = []
      })
      .addCase(fetchProductList.pending, (state) => {
        state.products = []
      })
      .addCase(
        fetchProductList.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.products = action.payload.map((product: IProduct) => ({
            label: product.productName,
            value: product.productId,
          }))
        }
      )
      .addCase(fetchProductList.rejected, (state) => {
        state.products = []
      })
      .addCase(fetchPlatformList.pending, (state) => {
        state.platforms = []
      })
      .addCase(
        fetchPlatformList.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.platforms = action.payload.map((platform: IPlatform) => ({
            label: platform.platformName,
            value: platform.platformId,
            platformCode: platform.platformCode,
          }))
        }
      )
      .addCase(fetchPlatformList.rejected, (state) => {
        state.platforms = []
      })
      .addCase(fetchPaymentProviderList.pending, (state) => {
        state.paymentProviders = []
      })
      .addCase(
        fetchPaymentProviderList.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.paymentProviders = action.payload.map(
            (provider: IPaymentProvider) => ({
              label: provider.providerName,
              value: provider.providerId,
            })
          )
        }
      )
      .addCase(fetchPaymentProviderList.rejected, (state) => {
        state.paymentProviders = []
      })
      .addCase(fetchPaymentModeList.pending, (state) => {
        state.paymentModes = []
      })
      .addCase(
        fetchPaymentModeList.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.paymentModes = action.payload.map((mode: IPaymentMode) => ({
            label: mode.paymentName,
            value: mode.paymentId,
          }))
        }
      )
      .addCase(fetchPaymentModeList.rejected, (state) => {
        state.paymentModes = []
      })
      .addCase(fetchPlanMetaData.pending, (state) => {
        state.contentPartners = []
        state.systems = []
        state.adPolicies = []
        // state.categories = []
      })
      .addCase(
        fetchPlanMetaData.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.contentPartners = action.payload.contentPartners.map(
            (partner: IContentPartner) => ({
              label: partner.contentPartnerName,
              value: partner.contentPartnerId,
            })
          )
          state.systems = action.payload.systems.map((system: ISystem) => ({
            label: system.systemName,
            value: system.systemName,
          }))
          state.adPolicies = action.payload.adPolicies.map(
            (policy: IAdPolicy) => ({
              label: policy.description,
              value: policy.policyId,
            })
          )
          //  NOTE: to be used in future
          // for plan categories
          // state.categories = action.payload?.categories?.map(
          //   (category: { category: string; title: string }) => ({
          //     label: category.title,
          //     value: category.category,
          //   })
          // )
        }
      )
      .addCase(fetchPlanMetaData.rejected, (state) => {
        state.contentPartners = []
        state.systems = []
        state.adPolicies = []
      })
  },
})

export default commonDataSlice.reducer
